import { Container, IDestroyOptions, Rectangle, Sprite, Text, TextStyle } from "pixi.js";
import { Event } from "../../../../../../../../shared/event-system/Event";
import { ITokenFrame } from "./ITokenFrame";
import { MyWalletResourcePackage } from "../../../../resources/MyWalletResourcePackage";
import { GameFont } from "../../../../../../common/GameFont";
import { Web3Model } from "../../../../../../../../web3/models/Web3Model";
import gsap from "gsap";
import { ButtonWithScaleAnimation } from "../../../../../../common/components/ButtonWithScaleAnimation";
import { CommonResourcesPackage } from "../../../../../../common/resources/CommonResourcesPackage";

export class ReceiveErc20Frame extends Container implements ITokenFrame {
    private static readonly COPY_TEXT = "Address was copied!";

    public get onBackButtonClick(): Event<void> {
        return <Event<void>><any>this.backButton.onUp;
    }

    private readonly backButton: ButtonWithScaleAnimation;
    private readonly background: Sprite;
    private readonly infoText: Text;
    private readonly copyIcon: Sprite;
    private readonly addressText: Text;
    private readonly notificationText: Text;
    private readonly showTimeline: gsap.core.Timeline;

    public constructor(
        tokenName: string,
        commonResources: CommonResourcesPackage,
        myWalletResources: MyWalletResourcePackage,
        private readonly web3Model: Web3Model
    ) {
        super();
        this.backButton = new ButtonWithScaleAnimation(commonResources.assets_common_ui_back_png);
        this.backButton.defaultView.height = 52;

        this.background = new Sprite(myWalletResources.assets_views_my_wallet_ui_mon_receive_frame_png);
        this.background.position.set(-67, 41);
        this.backButton.position.set(this.background.position.x - this.backButton.width - 28, this.background.position.y + 11);
        this.backButton.hitArea = new Rectangle(-this.backButton.width * 0.5, -this.backButton.height * 0.5, this.backButton.width * 2, this.backButton.height * 2);

        this.infoText = new Text(`Send only ${tokenName} to this address,\nsending any other coins\nmay result in permanent loss.`, new InfoTextStyle());
        this.infoText.position.set(this.background.position.x + (this.background.width - this.infoText.width) * 0.5, this.background.position.y + 69);

        this.copyIcon = new Sprite(myWalletResources.assets_views_my_wallet_ui_copy_address_icon_png);
        this.copyIcon.position.set(this.background.position.x + 75, this.infoText.position.y + this.infoText.height + 125);
        this.copyIcon.interactive = true;

        let address = this.web3Model.smartWalletAddress;
        if (address.length >= 23) {
            address = `${address.substring(0, 23)}:\n${address.substring(address.length - 8, address.length)}`;
        }
        this.addressText = new Text(address, new AddressTextStyle());
        this.addressText.position.set(this.copyIcon.position.x + this.copyIcon.width + 25, this.copyIcon.position.y + (this.copyIcon.height - this.addressText.height) * 0.5);
        this.addressText.interactive = true;

        this.notificationText = new Text(ReceiveErc20Frame.COPY_TEXT, new CopyTextStyle());
        this.notificationText.position.set(120, 585);
        this.notificationText.alpha = 0;

        this.addChild(
            this.backButton,
            this.background,
            this.infoText,
            this.copyIcon,
            this.addressText,
            this.notificationText
        );

        this.showTimeline = new gsap.core.Timeline({ paused: true });

        this.copyIcon.on("pointerup", this.onCopyAddressClick, this);
        this.addressText.on("pointerup", this.onCopyAddressClick, this);
    }

    public override destroy(options?: IDestroyOptions | boolean): void {
        this.showTimeline.kill();
        this.copyIcon.off("pointerup", this.onCopyAddressClick, this);
        this.addressText.off("pointerup", this.onCopyAddressClick, this);
        super.destroy(options);
    }

    public async show(): Promise<void> {
        this.showTimeline.clear();
        this.showTimeline.fromTo(this.background, { alpha: 0 }, { alpha: 1, duration: 0.3 }, "<");
        this.showTimeline.fromTo(this.infoText, { alpha: 0 }, { alpha: 1, duration: 0.3 }, "<");
        this.showTimeline.fromTo(this.addressText, { alpha: 0 }, { alpha: 1, duration: 0.3 }, "<");
        this.showTimeline.fromTo(this.copyIcon, { alpha: 0 }, { alpha: 1, duration: 0.3 }, "<");
        await this.showTimeline.play();
    }

    public async hide(): Promise<void> {
        await this.showTimeline.reverse();
    }

    private async onCopyAddressClick(): Promise<void> {
        window.FixedClipboard.copy(this.web3Model.smartWalletAddress);
        const animationTimeLine = new gsap.core.Timeline();
        animationTimeLine.to(this.notificationText, { alpha: 1, duration: 0.3 });
        animationTimeLine.to(this.notificationText, { alpha: 0, duration: 0.3, delay: 1 }, ">");
        await animationTimeLine.play();
        animationTimeLine.kill();
    }
}

class InfoTextStyle extends TextStyle {
    public constructor() {
        super({
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 48,
            fill: "#FFFFFF",
            align: "center"
        });
    }
}

class AddressTextStyle extends TextStyle {
    public constructor() {
        super({
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 42,
            fontWeight: '400',
            fill: "#67EBFF"
        });
    }
}

class CopyTextStyle extends TextStyle {
    public constructor() {
        super({
            fill: "#fefeff",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 40
        });
    }
}
